<template>
  <el-dialog
    v-if="dialogVisible"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :show-close="false"
    title="审核通过"
    width="460px"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <ElForm v-if="auditType === IMAGE_TYPES.REMIX_AUDIT_PASS" ref="formRef" label-width="130px" :model="passFormData" :rules="rules">
      <ElFormItem label="视频">
        <span>已选择{{ idList.length }}个视频</span>
      </ElFormItem>

      <ElFormItem label="设计师" prop="opId">
        <ThrottleSelector style="width: 100%" v-model="passFormData.opId" :list="sysUsers" filterable clearable placeholder="选择设计师" />
      </ElFormItem>

      <ElFormItem label="策划师" prop="plannerId">
        <ThrottleSelector
          style="width: 100%"
          v-model="passFormData.plannerId"
          :list="sysUsers"
          filterable
          clearable
          placeholder="选择设计师"
        />
      </ElFormItem>

      <GameGroupFormItems :form-data="passFormData" selector-width="100%" @group-change="handleGroup"></GameGroupFormItems>

      <ElFormItem label="素材样式"> 根据元视频自动生成 </ElFormItem>

      <ElFormItem label="营销主题标签"> 根据元视频自动生成 </ElFormItem>

      <ElFormItem label="内容标签"> 根据元视频自动生成 </ElFormItem>
    </ElForm>

    <ElForm v-else ref="formRef" label-width="130px" :model="denyFormData" :rules="rules">
      <ElFormItem label="视频">
        <span>已选择{{ idList.length }}个视频</span>
      </ElFormItem>

      <ElFormItem label="拒绝理由" prop="rejectReasonIds">
        <el-select
          style="width: 100%"
          v-model="denyFormData.rejectReasonIds"
          filterable
          multiple
          collapse-tags
          clearable
          placeholder="选择拒绝理由"
        >
          <el-option v-for="item in REJECT_REASONS" :key="item.value" :value="item.value" :label="item.label"></el-option>
        </el-select>
      </ElFormItem>

      <ElFormItem label="详细描述" prop="rejectDetailInfo">
        <el-input type="textarea" :rows="5" placeholder="请输入内容" v-model="denyFormData.rejectDetailInfo"> </el-input>
      </ElFormItem>
    </ElForm>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="onClose">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="onConfirm"> 确定 </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import Cookies from 'js-cookie'
import { Message } from 'view-design'
import { ref, computed } from 'vue'
import { MessageBox } from 'element-ui'
import { isArray } from '@/common/js/utils'
import GameGroupFormItems from '@/views/material/uploading/components/game-group-form-items.vue'
import ThrottleSelector from '@/components/throttle-select/index.vue'

import { initPageData, SYS_TAGS, SYS_TAG_GROUPS, SYS_USERS, GAME_GROUPS, GAMES } from '@/data/global-opts'
import { abVideosAudit } from '@/api/material.model'
import { IMAGE_TYPES_STR_VAL as IMAGE_TYPES } from '@/views/material/uploading/consts.js'
import { REJECT_REASONS } from '../const.js'

const PAGE_REQUIRED_OPTS = initPageData([SYS_USERS, GAME_GROUPS, GAMES, SYS_TAGS, SYS_TAG_GROUPS])
const sysUsers = computed(PAGE_REQUIRED_OPTS.SYS_USERS)
const gameGroups = computed(PAGE_REQUIRED_OPTS.GAME_GROUPS)
const defaultOpId = Cookies.get('ch_op_id')

// eslint-disable-next-line
const props = defineProps({
  visible: {
    type: Boolean,
    default: ''
  },
  idList: {
    type: Array,
    default: () => []
  },
  auditType: {
    type: String,
    default: ''
  }
})

const getForm = () => {
  return {
    group: [],
    game: [],
    opId: Number(defaultOpId),
    plannerId: Number(defaultOpId)
  }
}
// eslint-disable-next-line
const emits = defineEmits(['update:visible', 'audit-finished'])

const formRef = ref(null)
const submitLoading = ref(false)
const rules = {
  opId: [{ required: true, message: '请选择设计师' }],
  game: [
    {
      required: true,
      validator(rule, value, callback) {
        if (!value?.length) {
          callback(new Error('游戏不能为空'))
        }

        callback()
      }
    }
  ],
  rejectReasonIds: [
    { required: true, message: '请选择拒绝理由', trigger: 'change' },
    { type: 'array', min: 1, message: '至少选择一个拒绝理由', trigger: 'change' }
  ],
  rejectDetailInfo: [
    { required: true, message: '请填写审核拒绝信息详情', trigger: 'change' },
    { min: 1, max: 200, message: '审核拒绝信息详情最多200个字' }
  ]
}

const passFormData = ref({ ...getForm() })
const denyFormData = ref({
  rejectReasonIds: [],
  rejectDetailInfo: ''
})

const dialogVisible = computed({
  get() {
    return props.visible
  },
  set(val) {
    emits('update:visible', val)
  }
})

function handleGroup(val) {
  const games = []
  const oldGames = JSON.parse(JSON.stringify(passFormData.value.game))
  if (isArray(val) && val.length) {
    val.forEach((ret) => {
      const index = gameGroups.value.findIndex((met) => met.id === ret)
      if (index > -1) {
        const { gameIds } = gameGroups.value[index]
        const child = gameIds.filter((met) => !oldGames.includes(parseInt(met))).map((met) => parseInt(met))
        games.push(...child)
      }
    })
    games.length && passFormData.value.game.push(...games)
  }
  // 切换游戏组时清空已选内容标签结果
  passFormData.value.label = []
}

function onClose() {
  passFormData.value = getForm()
  denyFormData.value = {
    rejectReasonIds: [],
    rejectDetailInfo: ''
  }
  formRef.value?.clearValidate()
  dialogVisible.value = false
}

function onConfirm() {
  formRef.value.validate((valid) => {
    if (valid) {
      submitLoading.value = true
      const { opId, game, plannerId } = passFormData.value
      ;(props.auditType === IMAGE_TYPES.REMIX_AUDIT_DENY
        ? abVideosAudit({
          imageDetailIds: props.idList,
          rejectReasonIds: denyFormData.value.rejectReasonIds.join(','),
          rejectDetailInfo: denyFormData.value.rejectDetailInfo,
          type: IMAGE_TYPES.REMIX_AUDIT_DENY
        })
        : abVideosAudit({
          imageDetailIds: props.idList,
          designerId: opId,
          plannerId,
          gameIds: game.join(','),
          type: IMAGE_TYPES.REMIX_AUDIT_PASS
        })
      )
        .then((data) => {
          const failedItems = data?.filter((i) => !i.success)
          if (failedItems?.length) {
            const msgList = failedItems.map((i) => i.msg)
            MessageBox.alert(`<p style="max-height:200px;overflow:auto;">${msgList.join('<br/>')}</p>`, '提示', {
              dangerouslyUseHTMLString: true,
              confirmButtonText: '确定'
            })
          } else {
            Message.success('审核成功')
          }
          emits('audit-finished')
          onClose()
        })
        .finally(() => {
          submitLoading.value = false
        })
    }
  })
}
</script>

<style scoped lang="scss"></style>
